import {
  Component,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ResultsUtilsService } from '@app/modules/reports/services/results-utils.service';
import { ResultsService } from '@app/modules/reports/services/results.service';
import { SetResultsAction } from '@app/modules/reports/store/results.actions';
import { Result } from '@app/modules/reports/types/result.types';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { ToastService } from '@shared/components/toast-container/toast.service';
import { map, switchMap } from 'rxjs';

@Component({
  selector: 'app-result-search',
  imports: [FormsModule, ReactiveFormsModule, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './result-search.component.html',
})
export class ResultSearchComponent {
  private dialogRef = inject(NgbActiveModal);
  private store = inject(Store);
  private toastService = inject(ToastService);
  private resultsService = inject(ResultsService);
  private resultsUtilsService = inject(ResultsUtilsService);
  private translateService = inject(TranslateService);

  protected kitTokenControl = new FormControl<string>('', [
    Validators.required,
  ]);
  protected loading = signal(false);

  protected search() {
    if (this.kitTokenControl.invalid) {
      return;
    }

    this.loading.set(true);

    this.resultsService
      .listResults(
        this.resultsUtilsService.buildHttpParams({
          kitIdToken: this.kitTokenControl.value,
        }),
        {}
      )
      .pipe(
        switchMap((result) =>
          this.store
            .dispatch(new SetResultsAction(result.items))
            .pipe(map(() => result))
        )
      )
      .subscribe({
        next: (result) => {
          const results = result.items;
          if (results.length === 1) {
            this.resultsUtilsService.openResultDetails(results[0].id);
            this.dismiss();
          } else if (results.length > 1 && results.length < 100) {
            this.resultsUtilsService.openResultSearchResultsDialog(
              results.map((result: Result) => result.id)
            );
            this.dismiss();
          } else if (results.length >= 100) {
            this.toastService.danger(
              this.translateService.instant(
                'results.search.feedback.tooManyResults'
              ),
              this.translateService.instant(
                'results.search.feedback.tooManyResultsTitle'
              ),
              5000
            );
          } else {
            this.toastService.danger(
              this.translateService.instant('results.search.feedback.notFound'),
              this.translateService.instant(
                'results.search.feedback.notFoundTitle'
              ),
              5000
            );
          }
          this.loading.set(false);
        },
        error: () => {
          this.toastService.danger(
            this.translateService.instant('results.search.feedback.notFound'),
            this.translateService.instant(
              'results.search.feedback.notFoundTitle'
            ),
            5000
          );
          this.loading.set(false);
        },
      });
  }

  dismiss() {
    this.dialogRef.close();
  }
}
